<template>
  <div class="search_bar--row d-flex pt-1" :style="[styles]"
    v-click-outside="hideTooltips"
  >
    <form
      @submit.prevent="handleKeywordChanged(currentKeyword)"
      autocomplete="off"
      class="search_bar_form d-flex flex-column flex-grow-1"
      :class="{ 'show-border': tipsVisible }"
    >
      <search-category
        v-if="!isMinimal"
        class="show-mobile mb-1"
        :isIndexesLoaded="isIndexesLoaded"
        :indexes="indexes"
        :state="state"
      />

      <span class="search_bar_input flex-grow-1 bg-transparent d-flex justify-content-between align-items-stretch">
        <search-by-field-button
          v-if="!isMinimal && withSearchBy"
          class="pl-2 pr-1"
          :index="state.index"
          :activeFields="state.search_by_fields"
        />

        <input type="text"
          v-model="currentKeyword"
          class="input__field form-control mt-0 bg-white"
          aria-describedby="search"
          ref="searchInput"
          :placeholder="placeholderText"
          @keyup="handleKeyDown(currentKeyword)"
        />

        <div class="search_bar_tips t-sm cursor-pointer t-blue"
          @click="toggleTips"
          :style="{ opacity: tipsVisible ? 1 : null }"
          :class="{ 'is-minimal': isMinimal }"
        >{{ tipsVisible ? 'Hide' : 'Show' }} Tips (AND, OR, ...)</div>

        <!-- Synonyms toggle button -->
        <a class="search_bar_input--inner-button t-sm t-primary pr-2"
          v-if="!isMinimal && hasSynonyms"
          v-tooltip.bottom="'Show synonyms'"
          @click="toggleSynonyms"
        >
          <button class="btn btn-sm py-0 t-600 btn-outline-primary" type="button">
            <template v-if="state.ignore_synonyms"><s>Synonyms</s></template>
            <template v-else>+ Synonyms</template>
          </button>
        </a>

        <exclude-button
          v-if="!isMinimal"
          :class="'d-none d-sm-block pr-2'"
          :currentExcludes="currentExcludes"
          @setExcludes="setExcludes"
        />

        <tips
          v-if="tipsVisible"
          :searchInput="$refs.searchInput"
          :currentKeyword="currentKeyword"
          @hide="tipsVisible = false"
          @updateKeyword="handleKeyDown"
        />

        <!-- Submit search button -->
        <button
          type="submit"
          class="btn search-button-header-color t-600"
        ><i class="fa fa-search pr-2" /><span class="t-white">Search</span></button>
      </span>
    </form>

    <div v-if="showSavedSearch && $currentUser" class="search_save">
      <button @click.prevent="handleSaveSearch()" type="button"
        :class="`btn btn-save-search t-sm ml-2 t-600 ${(isSearchSaved || !isResultLoaded) ? 'disabled' : ''}`"
        v-tooltip.bottom="{
          content: alertTooltip,
          show: error,
          trigger: (isSearchSaved || !error) ? 'hover' : 'manual',
          placement: 'bottom',
          classes: 'width--235px t-xs'
        }"
      >
        <i v-if="isRequest" class="fa fa-spinner fa-spin fa-spin-left t-xxs" />
        <span v-if="!isSearchSaved" class="position-relative"><i class="fa fa-bell t-sm pr-2" />Create Alert</span>
        <span v-if="isSearchSaved" class="position-relative">Alert Created</span>
      </button>
    </div>
    <div v-if="showSavedSearch && !$currentUser" class="search_save">
      <button @click.prevent="handleSaveSearch()" type="button"
        :class="`btn btn-save-search t-sm ml-2 t-600 ${!isResultLoaded ? 'disabled' : ''}`"
        v-tooltip.bottom="{
          content: alertTooltip,
          placement: 'bottom',
          classes: 'width--235px t-xs'
        }"
      >
        <span class="text-nowrap"><i class="fa fa-bell t-sm pr-2" />Create Alert</span>
        <i class="fa fa-lock semi-transparent ml-2" />
      </button>
    </div>
  </div>
</template>

<script>
import MPService from '@/services/mixpanel'

import SearchCategory from '@/components/search/Category'
import Tips from '@/components/search/Tips'
import ExcludeButton from '@/components/search/ExcludeButton'
import SearchByFieldButton from '@/components/search/SearchByFieldButton'

import placeholderGenerator from '@/mixins/search/placeholderGenerator'

import { mapActions } from 'vuex'

export default {
  name: 'searchBar',
  mixins: [placeholderGenerator],
  components: {
    SearchCategory,
    ExcludeButton,
    SearchByFieldButton,
    Tips
  },
  props: {
    isIndexesLoaded: {
      type: Boolean,
      required: false
    },
    isResultLoaded: {
      type: Boolean,
      required: false
    },
    indexes: {
      type: Object,
      required: false
    },
    isScopedSearch: {
      type: Boolean,
      default: false
    },
    totalPossibleResults: {
      type: Number,
      default: 0
    },
    triggerSearchBarFocus: {
      type: Boolean,
      default: false
    },
    state: {
      type: Object,
      required: true
    },
    isMinimal: {
      type: Boolean,
      required: false,
      default: false
    },
    withSearchBy: {
      type: Boolean,
      required: false,
      default: true
    },
    isSticky: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      error: null,
      isRequest: false,
      isSearchSaved: false,
      currentKeyword: '',
      currentExcludes: [],
      styles: {},
      tipsVisible: false
    }
  },
  mounted () {
    this.currentKeyword = this.state.keyword || ''
    this.currentExcludes = this.state.exclude || []
    this.state.saved ? this.isSearchSaved = true : this.isSearchSaved = false
    this.$nextTick(() => {
      this.setFocus()
      if (this.isWithFixedBar) {
        this.handlePositionFixed()
        window.addEventListener('resize', this.handlePositionFixed)
      }
    })
  },
  watch: {
    'state.keyword' (newVal, oldVal) {
      if (newVal !== oldVal) this.currentKeyword = this.state.keyword
    },
    'state.exclude' (newVal, oldVal) {
      if (newVal !== oldVal) this.currentExcludes = this.state.exclude || []
    },
    'state.saved' (newVal, oldVal) {
      if (newVal !== oldVal) this.state.saved ? this.isSearchSaved = true : this.isSearchSaved = false
    },
    triggerSearchBarFocus (newVal, oldVal) {
      if (newVal !== oldVal) this.setFocus()
    },
    isResultLoaded (newVal, oldVal) {
      // If we perform a new search, we want to update the local keyword with the keyword used in the search
      if (newVal !== oldVal && !newVal) {
        this.currentKeyword = this.state.keyword
      }
      if (newVal !== oldVal && newVal) {
        this.$nextTick(() => { if (this.isWithFixedBar) this.handlePositionFixed() })
      }
    },
    currentKeyword (keyword) {
      this.$emit('keywordTyped', keyword)
    }
  },
  computed: {
    isWithFixedBar () {
      return !this.isMinimal && this.isSticky
    },
    hasSynonyms () {
      return this.state.synonyms && Object.entries(this.state.synonyms).length > 0
    },
    alertTooltip () {
      if (this.error) {
        return this.error
      } else {
        if (this.isSearchSaved) {
          return 'Your query is saved. We will notify you by email when new results are added.'
        }
        return 'Save your query and get notified by email when new results are added.'
      }
    },
    showSavedSearch () {
      // if is default filter provided
      if (this.isScopedSearch || this.isMinimal) {
        return false
      }

      // if keyword is true, we can save the search
      if (this.state.keyword) {
        return true
      }

      // check if we have an active filter
      if (this.state.filters) {
        for (let filter in this.state.filters) {
          if (!this.isEmpty(this.state.filters[filter].buckets)) {
            for (let bucket of this.state.filters[filter].buckets) {
              if (bucket.active) {
                return true
              }
            }
          }
        }
      }

      return false
    }
  },
  methods: {
    ...mapActions('alerts', [
      'createAlert'
    ]),
    setExcludes (excludes) {
      this.$emit('excludesChanged', excludes)
    },
    hideTooltips () {
      this.tipsVisible = false
    },
    toggleTips () {
      this.tipsVisible = !this.tipsVisible
      if (this.tipsVisible) {
        MPService.trackEvent('show_tips')
      }
    },
    toggleSynonyms () {
      this.$events.$emit('modal:synonyms:open', this.state)

      // this.synonymsVisible = !this.synonymsVisible
      // this.tipsVisible = false

      MPService.trackEvent('synonyms')
    },
    setFocus () {
      this.$nextTick(() => {
        // Don't trigger this on mobile
        if (!this.isMobile() && this.$refs.searchInput) this.$refs.searchInput.focus()
      })
    },
    handleSaveSearch () {
      if (this.isEmpty(this.$currentUser)) {
        this.$events.$emit('modal:login:open')
        return
      }
      if (this.isSearchSaved || this.isRequest) return

      this.isRequest = true
      this.error = null
      const search = {
        data: {}
      }

      search.data.filter_order = this.state.filter_order
      search.data.filters = this.state.filters
      search.data.keyword = this.state.keyword
      search.data.per_page = this.state.per_page
      search.data.index = this.state.index
      search.data.global = this.state.global
      search.data_keyword = this.state.keyword
      search.data_index = this.state.index
      search.data_global = this.state.global
      search.data.exclude = this.state.exclude
      search.data.search_by_fields = this.state.search_by_fields || []
      search.data.ignore_synonyms = this.state.ignore_synonyms || false

      search.name = this.state.keyword || 'Empty search'
      search.results = this.totalPossibleResults

      this.createAlert({ alert: search }).then(savedSearch => {
        this.$events.$emit('search:changed:saved', savedSearch.id)
        this.isRequest = false
      }).catch(err => {
        this.isRequest = false
        this.error = this.handleError(err)
      })
    },
    handleKeywordChanged (item) {
      this.tipsVisible = false
      this.error = null
      this.$emit('excludesChanged', this.currentExcludes, true)
      this.$emit('keywordChanged', item)
      this.currentKeyword = item
    },
    handleKeyDown (item) {
      this.currentKeyword = item
      this.$emit('keywordTyped', item)
    },
    handlePositionFixed () {
      if (this.isStickySupported()) {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
        if (width > 786) {
          this.$set(this.styles, 'top', `96px`)
        } else {
          // On smaller screens, we have a different offset as we do on more wide screens
          this.$set(this.styles, 'top', `55px`)
        }
        this.$set(this.styles, 'position', `sticky`)
      } else {
        this.$set(this.styles, 'top', `0`)
        this.$set(this.styles, 'position', `relative`)
      }
    }
  },
  destroyed () {
    if (this.isWithFixedBar) window.removeEventListener('resize', this.handlePositionFixed)
  }
}
</script>
